import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dashboardService, userService } from "../../services/api";
import { FaArrowRight } from "react-icons/fa";

const BoltIcon = (props) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M5.5 0L1 5.5h3.5L4 10l4.5-5.5H5L5.5 0z"
      fill="#E11E4A"
    />
  </svg>
);

const formatPosition = (pos) => {
  if (!pos || typeof pos !== "string") return "-";
  return pos
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("-");
};

const formatTenderType = (t) => {
  if (!t || typeof t !== "string") return "-";
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
};

// Pad a count to a 2-digit "04" string like the mockup. Leaves 10+ as-is.
const padCount = (n) => {
  const num = Number(n) || 0;
  return num < 10 ? `0${num}` : String(num);
};

const ClubRecentTenders = () => {
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clubProfile, setClubProfile] = useState(null);

  useEffect(() => {
    (async () => {
      const [tenderRes, profileRes] = await Promise.all([
        dashboardService.getClubLatestTender(),
        userService.getProfile(),
      ]);
      if (tenderRes.success) setTender(tenderRes.data);
      if (profileRes.success) setClubProfile(profileRes.data);
      setLoading(false);
    })();
  }, []);

  // ── Loading skeleton (matches the new card layout) ────────────────
  if (loading) {
    return (
      <div className="bg-white border border-[#D6DBEA] rounded-lg p-7 h-full flex flex-col gap-5 shadow-[0px_5px_22px_rgba(0,0,0,0.04),0px_0px_0px_0.5px_rgba(0,0,0,0.03)]">
        <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
        <div className="flex items-center gap-4">
          <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
          <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex items-center gap-6">
          <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse" />
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-10 w-24 bg-gray-200 rounded animate-pulse" />
          <div className="h-10 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="h-px bg-[#E5E5E5]" />
        <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
        <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
        <div className="h-10 w-36 border border-[#E6E6E6] rounded-md animate-pulse" />
      </div>
    );
  }

  // ── Empty state (no tenders yet) ──────────────────────────────────
  if (!tender) {
    return (
      <div className="bg-white border border-[#D6DBEA] rounded-lg p-7 h-full flex flex-col gap-4 shadow-[0px_5px_22px_rgba(0,0,0,0.04),0px_0px_0px_0.5px_rgba(0,0,0,0.03)]">
        <span className="text-[12px] font-medium tracking-wider uppercase text-[#929292]">
          Recently created Request
        </span>
        <div className="flex-1 flex flex-col items-center justify-center gap-4 py-6">
          <p className="text-[#232323] text-base md:text-lg font-medium text-center">
            No Requests have been created yet.
          </p>
          <Link
            to="/club/tenders/create"
            className="inline-flex items-center gap-2 bg-[#2283FF] text-white px-5 py-2.5 rounded-lg hover:bg-blue-600 font-medium text-sm"
          >
            Create new Request
            <FaArrowRight className="text-white" />
          </Link>
        </div>
      </div>
    );
  }

  // ── Loaded state — new mockup layout ──────────────────────────────
  const position = formatPosition(tender?.position);
  const tenderType = formatTenderType(tender?.type_of_tender);
  const isBoosted = !!tender?.is_boosted;

  return (
    <div className="bg-white border border-[#D6DBEA] rounded-lg p-7 h-full flex flex-col gap-5 shadow-[0px_5px_22px_rgba(0,0,0,0.04),0px_0px_0px_0.5px_rgba(0,0,0,0.03)]">
      {/* Small grey header */}
      <span className="text-[12px] font-medium tracking-wider uppercase text-[#929292]">
        Recently created Request
      </span>

      {/* Title row: position + optional Boosted pill */}
      <div className="flex items-center gap-4 flex-wrap">
        <h2 className="text-xl font-bold text-[#242426] leading-tight m-0">
          {position}
        </h2>
        {isBoosted && (
          <span
            className="inline-flex items-center gap-1 rounded-md px-2 py-1"
            style={{
              background:
                "linear-gradient(90deg, rgba(255, 77, 79, 0.2) 0%, rgba(250, 170, 0, 0.2) 100%)",
            }}
          >
            <BoltIcon />
            <span className="text-[10px] font-medium uppercase text-black tracking-wide">
              Boosted
            </span>
          </span>
        )}
      </div>

      {/* Club + Position + Tender Type row */}
      <div className="flex items-center justify-between w-[90%] gap-8 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {clubProfile?.club_details?.logo ? (
            <img
              src={clubProfile.club_details.logo}
              alt={clubProfile.club_details.name || "Club"}
              className="w-12 h-12 object-contain flex-shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-100 flex-shrink-0" />
          )}
          <span className="font-medium text-base text-black truncate">
            {clubProfile?.club_details?.name || "—"}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-[#777777]">Position</span>
          <span className="font-medium text-base text-[#232323]">
            {position}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-[#777777]">Request Type</span>
          <span className="font-medium text-base text-[#232323]">
            {tenderType}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-[#E5E5E5]" />

      {/* Two count banners */}
      <div className="flex flex-col gap-4">
        <div className="bg-[#E6FFEB] border border-[#B3FFC0] rounded-xl px-4 py-2.5 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[1px] text-[#006849]">
            Total pitches received
          </span>
          <span className="text-base font-bold text-[#006849]">
            {padCount(tender?.pitches_received)}
          </span>
        </div>
        <div className="bg-[#E6F3FF] border border-[#B3D4FF] rounded-xl px-4 py-2.5 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[1px] text-[#0019A6]">
            Accepted pitches
          </span>
          <span className="text-base font-bold text-[#0019A6]">
            {padCount(tender?.accepted_pitches)}
          </span>
        </div>
      </div>

      {/* View Tender button */}
      <Link
        to={`/club/tenders/${tender?.id}/pitches`}
        className="inline-flex items-center justify-center gap-2 self-start bg-white border border-[#E6E6E6] rounded-md px-3 py-2.5 text-[#1E1E1E] text-base hover:bg-gray-50 transition"
      >
        View Request
        <FaArrowRight className="text-[#1E1E1E] text-xs" />
      </Link>
    </div>
  );
};

export default ClubRecentTenders;
